import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useCalls } from "@/CallsContext";
import { useGoals } from "@/GoalsContext";
import {
  breakdownByCompany,
  currencySymbol,
  formatTime,
  hms,
  minutesLabel,
  money,
  summarize,
  weeklyIncomePerformance,
  workPace,
} from "@/lib/callStats";
import { getMonthlyGoal, monthGoalSnapshot } from "@/lib/goals";

import { Link } from "react-router-dom";
import MonthlyGoalPanel from "@/components/MonthlyGoalPanel";
import { customerSubscriptionBadge } from "@/lib/subscriptionDisplay";

function Kpi({ label, value, hint, featured }) {
  return (
    <article className={`mpd-kpi ${featured ? "is-featured" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <p>{hint}</p> : null}
    </article>
  );
}

export default function OverviewPage() {
  const { calls, visible, license, error, loading, period } = useCalls();
  const { version } = useGoals();
  const symbol = currencySymbol(visible.length ? visible : calls);
  const stats = summarize(visible);
  const pace = workPace(visible, period);
  const goal = getMonthlyGoal();
  const goalSnap = monthGoalSnapshot(calls);
  const week = weeklyIncomePerformance(calls, period);
  const companies = breakdownByCompany(visible).slice(0, 5);
  const recent = [...visible].sort((a, b) => (b.startTime || 0) - (a.startTime || 0)).slice(0, 5);
  const maxCompany = companies[0]?.earnings || 1;
  const badge = customerSubscriptionBadge(license);
  const active = badge.on;
  const pct = week.pctChange;
  const pctLabel =
    pct == null ? "No prior week" : `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}% vs last week`;

  return (
    <div className="mpd-page">
      <div className="mpd-page-head">
        <div>
          <h1>Overview</h1>
          <p className="mpd-sub">{period.label}</p>
        </div>
        <span className={`mpd-license ${active ? "is-on" : "is-off"}`}>
          <strong>{badge.title}</strong>
          {badge.subtitle ? <em>{badge.subtitle}</em> : null}
        </span>
      </div>

      {error ? <p className="mp-error">{error}</p> : null}
      {loading ? <p className="mpd-muted">Loading calls…</p> : null}

      <section className="mpd-card mp-pay-banner">
        <div className="mpd-card-head">
          <div>
            <h2>Pay Rates</h2>
            <p>Set a new per-minute rate with an effective date and recalculate estimated earnings.</p>
          </div>
          <Link className="mpd-primary" to="/pay-rates">Open calculator</Link>
        </div>
      </section>

      <section className="mpd-kpis">
        <Kpi featured label="Estimated earnings" value={money(stats.earnings, symbol)} hint={period.label} />
        <Kpi label="Minutes" value={minutesLabel(stats.minutes)} hint="Worked time" />
        <Kpi label="Calls" value={String(stats.calls)} hint="Completed" />
        <Kpi label="Avg duration" value={stats.calls ? hms(stats.avgSec) : "—"} hint="Per call" />
      </section>

      <section className="mpd-card mpd-week-card">
        <div className="mpd-card-head">
          <div>
            <h2>Weekly income performance</h2>
            <p>Mon–Sun · {week.label}</p>
          </div>
          <span className={`mpd-delta ${pct == null ? "" : pct >= 0 ? "is-up" : "is-down"}`}>
            {pctLabel}
          </span>
        </div>
        <div className="mpd-week-stats">
          <div>
            <span>Highest day</span>
            <strong>{week.highest?.label || "—"} {money(week.highest?.earnings || 0, symbol)}</strong>
          </div>
          <div>
            <span>Lowest day</span>
            <strong>{week.lowest?.label || "—"} {money(week.lowest?.earnings || 0, symbol)}</strong>
          </div>
          <div>
            <span>Income dropped</span>
            <strong>
              {week.drops.length
                ? week.drops.map((row) => row.label).join(", ")
                : "No day-over-day drop"}
            </strong>
          </div>
        </div>
        <div className="mpd-chart mpd-chart-week">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={week.series} barGap={4} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e8eeeb" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5b6f69" }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: "#7a8c86" }}
                axisLine={false}
                tickLine={false}
                width={44}
                tickFormatter={(v) => `${symbol}${Math.round(v)}`}
              />
              <Tooltip
                formatter={(value, name) => [
                  money(Number(value) || 0, symbol),
                  name === "prevWeek" ? "Previous week" : "This week",
                ]}
                contentStyle={{ borderRadius: 12, border: "1px solid #e4ebe8" }}
              />
              <Bar dataKey="prevWeek" fill="#d5e4df" radius={[4, 4, 0, 0]} maxBarSize={18} />
              <Bar dataKey="earnings" radius={[4, 4, 0, 0]} maxBarSize={22}>
                {week.series.map((row) => (
                  <Cell
                    key={row.key}
                    fill={
                      row.dropped
                        ? "#c2410c"
                        : row.key === week.highest?.key && row.earnings > 0
                          ? "#0f6b5c"
                          : "#1f8f7a"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mpd-chart-legend">
          <i className="is-now" /> This week
          <i className="is-prev" /> Previous week
          <i className="is-drop" /> Day-over-day drop
        </p>
      </section>

      <section className="mpd-grid-2 mpd-grid-side">
        <article className="mpd-card">
          <div className="mpd-card-head">
            <h2>Monthly goal</h2>
            <p>{goalSnap.monthLabel}</p>
          </div>
          {goal > 0 ? (
            <MonthlyGoalPanel key={version} snapshot={goalSnap} symbol={symbol} compact />
          ) : (
            <p className="mpd-muted">Set one monthly income target in Goals. We’ll show earned, remaining, and daily pace.</p>
          )}
        </article>
        <article className="mpd-card">
          <div className="mpd-card-head">
            <h2>Work pace</h2>
            <p>{pace.elapsed} day{pace.elapsed === 1 ? "" : "s"} elapsed</p>
          </div>
          <ul className="mpd-pace">
            <li><span>Effective rate</span><strong>{money(pace.perHour, symbol)}/hr</strong></li>
            <li><span>Est. earnings / day</span><strong>{money(pace.perDay, symbol)}</strong></li>
            <li><span>Minutes / day</span><strong>{minutesLabel(pace.minutesPerDay)}</strong></li>
            <li><span>Calls / day</span><strong>{pace.callsPerDay.toFixed(1)}</strong></li>
          </ul>
        </article>
      </section>

      <section className="mpd-grid-2">
        <article className="mpd-card">
          <div className="mpd-card-head">
            <h2>Recent calls</h2>
            <p>{stats.calls} in period</p>
          </div>
          {recent.length === 0 ? (
            <p className="mp-empty">No calls in this period.</p>
          ) : (
            <ul className="mpd-recent">
              {recent.map((call) => (
                <li key={call.id}>
                  <div>
                    <strong>{call.companyName || call.platform || "Call"}</strong>
                    <p>{call.date} · {formatTime(call.startTime)}</p>
                  </div>
                  <div className="mpd-recent-right">
                    <strong>{money(call.earnings, call.currencySymbol || symbol)}</strong>
                    <p>{minutesLabel(call.durationMin ?? (call.durationSec || 0) / 60)} min</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>
        <article className="mpd-card">
          <div className="mpd-card-head">
            <h2>Company breakdown</h2>
            <p>By earnings</p>
          </div>
          {companies.length === 0 ? (
            <p className="mp-empty">No company totals in this period.</p>
          ) : (
            <ul className="mpd-companies">
              {companies.map((row) => (
                <li key={row.name}>
                  <div className="mpd-companies-row">
                    <strong>{row.name}</strong>
                    <span>{money(row.earnings, symbol)}</span>
                  </div>
                  <div className="mpd-bar">
                    <span style={{ width: `${Math.round((row.earnings / maxCompany) * 100)}%` }} />
                  </div>
                  <p className="mpd-muted">{row.calls} calls · {minutesLabel(row.minutes)} min</p>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>
    </div>
  );
}
